import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  TextField,
  Tooltip,
  Alert,
  CircularProgress,
  Collapse,
  Stack,
  Divider,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  useTheme,
  alpha,
  Badge,
} from '@mui/material';
import {
  NotificationsActive as BellActiveIcon,
  NotificationsNone as BellIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIconEl,
  Edit as EditIcon,
  OpenInNew as OpenIcon,
  Email as EmailIcon,
  CheckCircle as CheckIcon,
  Warning as WarnIcon,
  AccessTime as ClockIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import GavelIcon2 from '@mui/icons-material/Gavel';
import BalanceIcon from '@mui/icons-material/Balance';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ProcessAlert,
  listAlerts,
  createAlert,
  updateAlert,
  deleteAlert,
  checkAlertNow,
  getAlertMovements,
} from '../../services/processAlertService';
import { formatarCNJ, tribunais } from '../../services/datajudService';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (d?: string) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return d; }
};

const timeSince = (d?: string): string => {
  if (!d) return 'nunca verificado';
  const diff = Date.now() - new Date(d).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}min atrás`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h atrás`;
  const days = Math.floor(hrs / 24);
  return `${days}d atrás`;
};

const daysSince = (d?: string): number => {
  if (!d) return 0;
  return Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const ProcessAlertsPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [alerts, setAlerts] = useState<ProcessAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [movementsData, setMovementsData] = useState<Record<string, any>>({});
  const [loadingMovements, setLoadingMovements] = useState<string | null>(null);
  const [checkingId, setCheckingId] = useState<string | null>(null);

  // Add dialog
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newCNJ, setNewCNJ] = useState(searchParams.get('cnj') || '');
  const [newTribunal, setNewTribunal] = useState(searchParams.get('tribunal') || 'TJSP');
  const [newLabel, setNewLabel] = useState('');
  const [newEmailEnabled, setNewEmailEnabled] = useState(true);
  const [saving, setSaving] = useState(false);

  // Edit notes
  const [editingNotes, setEditingNotes] = useState<string | null>(null);
  const [notesValue, setNotesValue] = useState('');

  // ── Load ─────────────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listAlerts();
      setAlerts(data);
    } catch {
      setError('Não foi possível carregar os alertas.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Open add dialog if coming from search with CNJ prefilled
  useEffect(() => {
    if (searchParams.get('cnj')) setShowAddDialog(true);
  }, []);

  // ── Actions ───────────────────────────────────────────────────────────────

  const handleAdd = async () => {
    const cnj = newCNJ.replace(/\D/g, '');
    if (cnj.length !== 20) { setError('Número CNJ deve ter 20 dígitos.'); return; }
    setSaving(true);
    setError(null);
    try {
      const created = await createAlert({
        cnj_number: cnj,
        tribunal: newTribunal,
        label: newLabel || undefined,
        email_enabled: newEmailEnabled,
      });
      setAlerts((prev) => [created, ...prev]);
      setShowAddDialog(false);
      setNewCNJ('');
      setNewLabel('');
    } catch {
      setError('Erro ao criar alerta. Verifique o número CNJ e tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (a: ProcessAlert) => {
    try {
      const updated = await updateAlert(a.id, { is_active: !a.is_active });
      setAlerts((prev) => prev.map((x) => (x.id === a.id ? updated : x)));
    } catch { setError('Erro ao atualizar alerta.'); }
  };

  const handleToggleEmail = async (a: ProcessAlert) => {
    try {
      const updated = await updateAlert(a.id, { email_enabled: !a.email_enabled });
      setAlerts((prev) => prev.map((x) => (x.id === a.id ? updated : x)));
    } catch { setError('Erro ao atualizar alerta.'); }
  };

  const handleDelete = async (a: ProcessAlert) => {
    if (!window.confirm(`Remover alerta "${a.label || a.cnj_number}"?`)) return;
    try {
      await deleteAlert(a.id);
      setAlerts((prev) => prev.filter((x) => x.id !== a.id));
    } catch { setError('Erro ao remover alerta.'); }
  };

  const handleCheckNow = async (a: ProcessAlert) => {
    setCheckingId(a.id);
    try {
      const result = await checkAlertNow(a.id);
      await load();
      if (result.new_movements > 0) {
        alert(`${result.new_movements} nova(s) movimentação(ões) encontrada(s)!`);
      } else {
        alert('Nenhuma novidade encontrada.');
      }
    } catch { setError('Erro ao verificar processo.'); }
    finally { setCheckingId(null); }
  };

  const loadMovements = async (a: ProcessAlert) => {
    if (expandedId === a.id) { setExpandedId(null); return; }
    setExpandedId(a.id);
    if (movementsData[a.id]) return;
    setLoadingMovements(a.id);
    try {
      const data = await getAlertMovements(a.id);
      setMovementsData((prev) => ({ ...prev, [a.id]: data }));
    } catch {
      setMovementsData((prev) => ({ ...prev, [a.id]: { error: true } }));
    } finally {
      setLoadingMovements(null);
    }
  };

  const saveNotes = async (a: ProcessAlert) => {
    try {
      const updated = await updateAlert(a.id, { notes: notesValue });
      setAlerts((prev) => prev.map((x) => (x.id === a.id ? updated : x)));
      setEditingNotes(null);
    } catch { setError('Erro ao salvar notas.'); }
  };

  // ── Stats ─────────────────────────────────────────────────────────────────

  const activeCount = alerts.filter((a) => a.is_active).length;
  const withNews = alerts.filter(
    (a) => a.is_active && a.last_checked_at && daysSince(a.last_checked_at) < 1
  ).length;

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* ── Header ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <BellActiveIcon color="primary" />
            <Typography variant="h5" fontWeight={700}>
              Alertas de Processos
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Monitore processos e receba notificações quando houver novas movimentações.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Atualizar lista">
            <IconButton onClick={load} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowAddDialog(true)}
            sx={{ borderRadius: 2 }}
          >
            Novo Alerta
          </Button>
        </Box>
      </Box>

      {/* ── Summary cards ── */}
      {alerts.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Processos monitorados', value: alerts.length, color: 'primary.main', icon: <BalanceIcon /> },
            { label: 'Ativos', value: activeCount, color: 'success.main', icon: <CheckIcon /> },
            { label: 'Com e-mail ativo', value: alerts.filter(a => a.email_enabled && a.is_active).length, color: 'info.main', icon: <EmailIcon /> },
          ].map((s) => (
            <Grid item xs={6} sm={4} key={s.label}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  borderLeft: `4px solid ${s.color}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Box sx={{ color: s.color }}>{s.icon}</Box>
                <Box>
                  <Typography variant="h5" fontWeight={700} sx={{ color: s.color }}>
                    {s.value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {s.label}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* ── Error ── */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ── Loading ── */}
      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {/* ── Alert cards ── */}
      {!loading && alerts.length === 0 ? (
        <Paper
          sx={{
            p: 6,
            textAlign: 'center',
            borderRadius: 3,
            border: `1px dashed ${theme.palette.divider}`,
          }}
        >
          <BellIcon sx={{ fontSize: 56, color: 'grey.300', mb: 2 }} />
          <Typography variant="h6" gutterBottom color="text.secondary">
            Nenhum processo monitorado
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Adicione um processo para receber alertas quando houver novas movimentações.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowAddDialog(true)}
          >
            Adicionar processo
          </Button>
          <Box sx={{ mt: 3 }}>
            <Button
              variant="outlined"
              startIcon={<SearchIcon />}
              onClick={() => navigate('/search')}
            >
              Ir para pesquisa de processos
            </Button>
          </Box>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {alerts.map((a) => {
            const days = daysSince(a.last_movement_date);
            const isExpanded = expandedId === a.id;
            const movements = movementsData[a.id];

            return (
              <Card
                key={a.id}
                elevation={isExpanded ? 3 : 1}
                sx={{
                  borderRadius: 3,
                  border: `1px solid ${
                    !a.is_active
                      ? theme.palette.divider
                      : isExpanded
                        ? theme.palette.primary.main
                        : theme.palette.divider
                  }`,
                  opacity: a.is_active ? 1 : 0.6,
                  transition: 'all 0.2s',
                }}
              >
                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                  {/* ─ Card header ─ */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      {/* Label + chips */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                        <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1rem' }}>
                          {a.label || formatarCNJ(a.cnj_number)}
                        </Typography>
                        {!a.is_active && <Chip label="Pausado" size="small" variant="outlined" color="default" />}
                        {a.is_active && <Chip label="Ativo" size="small" color="success" />}
                        <Chip label={a.tribunal} size="small" color="primary" />
                      </Box>

                      {/* CNJ */}
                      {a.label && (
                        <Typography variant="body2" color="text.secondary" fontFamily="monospace" sx={{ mb: 0.5 }}>
                          {formatarCNJ(a.cnj_number)}
                        </Typography>
                      )}

                      {/* Status row */}
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <ClockIcon fontSize="small" color="action" />
                          <Typography variant="caption" color="text.secondary">
                            Verificado: {timeSince(a.last_checked_at)}
                          </Typography>
                        </Box>
                        {a.last_movement_count > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <GavelIcon2 fontSize="small" color="action" />
                            <Typography variant="caption" color="text.secondary">
                              {a.last_movement_count} movimentações
                            </Typography>
                          </Box>
                        )}
                        {a.last_movement_date && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="caption" color={days > 60 ? 'warning.main' : 'text.secondary'}>
                              Última mov.: {formatDate(a.last_movement_date).split(',')[0]}
                              {days > 30 && ` (${days}d atrás)`}
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      {/* Notes preview */}
                      {a.notes && editingNotes !== a.id && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1, fontStyle: 'italic', cursor: 'pointer' }}
                          onClick={() => { setEditingNotes(a.id); setNotesValue(a.notes || ''); }}
                        >
                          Notas: {a.notes}
                        </Typography>
                      )}

                      {/* Edit notes inline */}
                      {editingNotes === a.id && (
                        <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                          <TextField
                            size="small"
                            fullWidth
                            multiline
                            maxRows={3}
                            value={notesValue}
                            onChange={(e) => setNotesValue(e.target.value)}
                            placeholder="Suas notas sobre este processo..."
                            autoFocus
                          />
                          <Button size="small" variant="contained" onClick={() => saveNotes(a)}>Salvar</Button>
                          <Button size="small" onClick={() => setEditingNotes(null)}>Cancelar</Button>
                        </Box>
                      )}
                    </Box>

                    {/* Toggles */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-end' }}>
                      <Tooltip title={a.is_active ? 'Pausar monitoramento' : 'Reativar'}>
                        <Switch
                          checked={a.is_active}
                          onChange={() => handleToggleActive(a)}
                          size="small"
                          color="success"
                        />
                      </Tooltip>
                      <Tooltip title={a.email_enabled ? 'E-mail ativo' : 'E-mail pausado'}>
                        <IconButton
                          size="small"
                          color={a.email_enabled ? 'primary' : 'default'}
                          onClick={() => handleToggleEmail(a)}
                        >
                          <EmailIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  {/* ─ Action bar ─ */}
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      mt: 2,
                      pt: 2,
                      borderTop: `1px solid ${theme.palette.divider}`,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={
                        checkingId === a.id
                          ? <CircularProgress size={14} />
                          : <RefreshIcon />
                      }
                      onClick={() => handleCheckNow(a)}
                      disabled={checkingId === a.id || !a.is_active}
                    >
                      Verificar agora
                    </Button>

                    <Button
                      size="small"
                      variant="text"
                      startIcon={isExpanded ? <CollapseIconEl /> : <ExpandIcon />}
                      onClick={() => loadMovements(a)}
                    >
                      {isExpanded ? 'Ocultar' : 'Ver movimentações'}
                    </Button>

                    <Button
                      size="small"
                      variant="text"
                      startIcon={<EditIcon />}
                      onClick={() => { setEditingNotes(a.id); setNotesValue(a.notes || ''); }}
                    >
                      Notas
                    </Button>

                    <Tooltip title="Abrir no CNJ">
                      <IconButton
                        size="small"
                        component="a"
                        href={`https://www.cnj.jus.br/processo/${a.cnj_number}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <OpenIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Box sx={{ flex: 1 }} />

                    <Tooltip title="Remover alerta">
                      <IconButton size="small" color="error" onClick={() => handleDelete(a)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {/* ─ Movements panel ─ */}
                  <Collapse in={isExpanded}>
                    <Box sx={{ mt: 2 }}>
                      <Divider sx={{ mb: 2 }} />
                      {loadingMovements === a.id && <LinearProgress />}

                      {movements?.error && (
                        <Alert severity="warning">
                          Não foi possível carregar as movimentações. O processo pode não estar disponível no DataJud.
                        </Alert>
                      )}

                      {movements && !movements.error && (
                        <>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                              {movements.classe?.nome || 'Processo'} — {movements.orgaoJulgador?.nome || ''}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {movements.total_movimentos} movimentações totais
                            </Typography>
                          </Box>

                          <Box sx={{ maxHeight: 320, overflow: 'auto', pr: 1 }}>
                            {(movements.movimentos || []).map((m: any, idx: number) => (
                              <Box
                                key={idx}
                                sx={{
                                  display: 'flex',
                                  gap: 2,
                                  mb: 1.5,
                                  position: 'relative',
                                  '&:not(:last-child)::before': {
                                    content: '""',
                                    position: 'absolute',
                                    left: 11,
                                    top: 28,
                                    bottom: -6,
                                    width: 2,
                                    bgcolor: 'divider',
                                  },
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: '50%',
                                    bgcolor: idx === 0 ? 'primary.main' : 'grey.200',
                                    flexShrink: 0,
                                    mt: 0.3,
                                  }}
                                />
                                <Box sx={{ flex: 1 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                                    <Typography variant="body2" fontWeight={idx === 0 ? 700 : 400}>
                                      {m.nome}
                                      {idx === 0 && (
                                        <Chip label="ÚLTIMO" size="small" color="primary" sx={{ ml: 1, height: 18 }} />
                                      )}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {m.dataHora ? new Date(m.dataHora).toLocaleDateString('pt-BR') : ''}
                                    </Typography>
                                  </Box>
                                  {m.complemento && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                      {String(m.complemento).slice(0, 200)}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        </>
                      )}
                    </Box>
                  </Collapse>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}

      {/* ── Add Dialog ── */}
      <Dialog open={showAddDialog} onClose={() => setShowAddDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <BellIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Monitorar Processo
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Número CNJ *"
              placeholder="00000000000000000000"
              value={newCNJ}
              onChange={(e) => setNewCNJ(e.target.value)}
              helperText="20 dígitos (sem formatação ou com pontos/traços)"
              sx={{ mb: 2 }}
              size="small"
            />
            <TextField
              fullWidth
              select
              label="Tribunal *"
              value={newTribunal}
              onChange={(e) => setNewTribunal(e.target.value)}
              SelectProps={{ native: true }}
              sx={{ mb: 2 }}
              size="small"
            >
              {Object.entries(tribunais).map(([cod, info]) => (
                <option key={cod} value={cod}>{cod} — {info.nome}</option>
              ))}
            </TextField>
            <TextField
              fullWidth
              label="Rótulo (opcional)"
              placeholder="Ex: Caso Silva — Trabalhista"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              sx={{ mb: 2 }}
              size="small"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={newEmailEnabled}
                  onChange={(e) => setNewEmailEnabled(e.target.checked)}
                  color="primary"
                />
              }
              label="Receber alertas por e-mail"
            />
            <Alert severity="info" sx={{ mt: 1.5 }} icon={<BellIcon />}>
              Você será notificado quando houver novas movimentações no processo.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : <BellIcon />}
          >
            Monitorar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProcessAlertsPage;
